<template>
  <div class="container-fluid">
    <!-- Breadcrumb -->
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <!-- Header -->
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Internal User Management</h2>
          </div>

          <!-- Body -->
          <div class="body pt-0">
            <!-- Search and Filters -->
            <div
              class="chart-top-action d-lg-flex justify-content-between py-3"
            >
              <!-- Search Box -->
              <div class="mr-0 mr-lg-3 action-top-search">
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search by name, email, or phone"
                    aria-label="Search"
                    v-model="searchKeywords"
                    v-on:keyup.enter="searchUsers"
                    aria-describedby="search-users"
                  />
                  <div class="input-group-append" @click="searchUsers()">
                    <span class="input-group-text" id="search-users">
                      <i class="icon-magnifier"></i>
                    </span>
                  </div>
                </div>
              </div>
              <!-- Create User Button -->
              <template v-if="$store.getters['authpermission/hasAnyPermission'](['users.create'])">
                <div class="ml-auto">
                  <b-button variant="primary" @click="showCreateUserModal">
                    <i class="fa fa-plus"></i>
                    Create User
                  </b-button>
                </div>
              </template>
            </div>

            <!-- Users Table -->
            <b-table
              responsive
              table-class="mb-0 table-custom border"
              head-variant="light"
              :items="items"
              :fields="fields"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @sort-changed="onSortChanged"
              striped
              show-empty
            >
              <template #head()="{ label, field: { key } }">
                {{ label }}
                <template>
                  <i v-if="sortBy !== key" class=""></i>
                  <i
                    v-else-if="sortDesc"
                    class="fa fa-long-arrow-down ml-1"
                  ></i>
                  <i v-else class="fa fa-long-arrow-up ml-1"></i>
                </template>
              </template>

              <template #cell(name)="data">
                <span class="font-weight-bold">
                  {{ data.item.firstname }} {{ data.item.lastname }}
                </span>
              </template>

              <template #cell(status)="data">
                <p class="function-badge badge-group-table mb-0 text-center">
                  <span
                    class="badge ml-0 d-block"
                    :class="{
                      'badge-success':
                        data.value && data.value.toLowerCase() === 'active',
                      'badge-warning':
                        data.value &&
                        (data.value.toLowerCase() === 'inactive' ||
                          data.value.toLowerCase() === 'changepassword' ||
                          data.value.toLowerCase() === 'suspend'),
                      'badge-danger':
                        data.value &&
                        (data.value.toLowerCase() === 'locked' ||
                          data.value.toLowerCase() === 'disabled'),
                    }"
                  >
                    {{ data.value }}
                  </span>
                </p>
              </template>

              <template #cell(canProcessHqTx)="data">
                <div class="d-flex justify-content-center align-items-center">
                  <input
                    v-if="!isSuperAdmin(data.item.role)"
                    type="checkbox"
                    class="hq-tx-checkbox"
                    v-model="data.item.canProcessHqTx"
                    @change="onHqTxToggle(data.item)"
                  />
                </div>
              </template>

              <template #cell(isLockedOut)="data">
                <i v-if="data.value" class="fa fa-lock text-danger"></i>
                <i v-else class="fa fa-unlock text-success"></i>
              </template>

              <template #cell(actions)="data">
                <b-dropdown
                  v-if="canManageRow(data.item)"
                  text="Actions"
                  right
                  variant="outline-primary"
                  size="sm"
                >
                  <!-- Edit -->
                  <b-dropdown-item
                    v-if="canEdit"
                    @click="showEditUserModal(data.item)"
                  >
                    <i class="fa fa-pencil mr-1"></i> Edit
                  </b-dropdown-item>

                  <!-- Status / Lock actions -->
                  <template v-if="canChangeStatus && !isSelf(data.item)">
                    <b-dropdown-divider v-if="canEdit"></b-dropdown-divider>
                    <b-dropdown-header>Change Status</b-dropdown-header>
                    <b-dropdown-item
                      v-for="opt in statusOptions"
                      :key="opt.value"
                      :disabled="isCurrentStatus(data.item, opt.value)"
                      @click="changeStatus(data.item, opt.value, opt.label)"
                    >
                      {{ opt.label }}
                    </b-dropdown-item>

                    <b-dropdown-divider></b-dropdown-divider>

                    <b-dropdown-item
                      v-if="!isLocked(data.item)"
                      @click="lockUser(data.item)"
                    >
                      <i class="fa fa-lock text-danger mr-1"></i> Lock
                    </b-dropdown-item>
                    <b-dropdown-item v-else @click="unlockUser(data.item)">
                      <i class="fa fa-unlock text-success mr-1"></i> Unlock
                    </b-dropdown-item>
                  </template>
                </b-dropdown>
              </template>
            </b-table>

            <!-- Table Pagination Bottom -->
            <div
              class="d-flex py-3 align-items-center justify-content-between table-pagination"
            >
              <div class="per-page">
                <b-form-group
                  label="Per page"
                  label-for="per-page-select"
                  class="mb-0"
                >
                  <b-form-select
                    id="per-page-select"
                    v-model="perPage"
                    :options="pageOptions"
                    size="sm"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="number-page">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  first-text="First"
                  last-text="Last"
                  :per-page="perPage"
                  align="right"
                ></b-pagination>
              </div>
              <div class="go-page">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">Go To Page</span>
                  </div>
                  <input
                    type="text"
                    v-model="currentPageJump"
                    class="form-control"
                    @keyup.enter="onEnter"
                    onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create User Modal -->
    <create-user-modal
      ref="createUserModal"
      @user-created="onUserCreated"
    ></create-user-modal>

    <!-- Edit User Modal -->
    <edit-user-modal
      ref="editUserModal"
      @user-updated="onUserUpdated"
    ></edit-user-modal>

    <!-- Loader -->
    <main-loader :loading="isLoading"></main-loader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { InternalUserService } from "@/services/internalUserService";
import { UPDATE_INTERNAL_USER_HQ_TX } from "../../../store/actions.type.js";
import CreateUserModal from "./CreateUserModal.vue";
import EditUserModal from "./EditUserModal.vue";
import "@/plugins/sweetalert2";

const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;
const BreadCrumb = require("@/components/BreadCrumb.vue").default;

export default {
  name: "UserListComponent",
  components: {
    "main-loader": LoaderComponent,
    "bread-crumb": BreadCrumb,
    "create-user-modal": CreateUserModal,
    "edit-user-modal": EditUserModal,
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Home", links: "/home" },
        { title: "User Management", isbold: true },
      ],
      fields: [
        { key: "name", label: "Name", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "phone", label: "Phone", sortable: true },
        { key: "role", label: "Role", sortable: true },
        { key: "accessright", label: "Access Right", sortable: true },
        { key: "function", label: "Function", sortable: true },
        { key: "countryname", label: "Country", sortable: true },
        { key: "canProcessHqTx", label: "Process HQ", sortable: false },
        { key: "status", label: "Status", sortable: true },
        { key: "isLockedOut", label: "Locked", sortable: true },
        { key: "actions", label: "Actions", sortable: false },
      ],
      statusOptions: [
        { label: "Active", value: 1 },
        { label: "Suspend", value: 3 },
        { label: "Disabled", value: 4 },
        { label: "Change Password", value: 0 },
      ],
      items: [],
      currentPage: 1,
      perPage: 25,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      sortBy: "firstname",
      sortDesc: false,
      searchKeywords: "",
      currentPageJump: "",
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("internalUser", ["users", "totalUsers", "loading"]),
    canEdit() {
      return this.$store.getters["authpermission/hasAnyPermission"](["users.update"]);
    },
    canChangeStatus() {
      return this.$store.getters["authpermission/hasAnyPermission"](["users.status"]);
    },
    isSuperAdminUser() {
      return this.$store.getters["authpermission/hasAnyRole"](["SuperAdmin", "superadmin"]);
    },
    currentUserId() {
      // The authpermission module never persists state.user (its SET_USER mutation
      // is disabled), so read the flat "userid" key kept in sync by SET_CURRENT_USER.
      return localStorage.getItem("userid") || null;
    },
  },
  watch: {
    currentPage() {
      this.fetchUsers();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchUsers();
    },
  },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    ...mapActions("internalUser", [
      "setUsers",
      "setTotalUsers",
      "setLoading",
      "setCurrentPage",
      "setPerPage",
      "setSort",
      "setSortBy",
      "setSearchKeyword",
    ]),

    async fetchUsers() {
      this.isLoading = true;
      this.setLoading(true);

      try {
        const params = {
          page: this.currentPage,
          size: this.perPage,
          sort: this.sortDesc ? "desc" : "asc",
          sortby: this.sortBy,
        };

        if (this.searchKeywords && this.searchKeywords.trim() !== "") {
          params.search = this.searchKeywords.trim();
        }

        const response = await InternalUserService.getUsers(params);

        if (response.data && response.data.details) {
          this.items = response.data.details.map((user) => ({
            userid: user.userid,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            phone: user.phone,
            isPhoneConfirmed: user.isPhoneConfirmed,
            twofactorenabled: user.twofactorenabled,
            canProcessHqTx: !!user.canProcessHqTx,
            createdby: user.createdby,
            status: user.status,
            isLockedOut: user.isLockedOut,
            role: user.role,
            accessright: user.accessright,
            function: user.function,
            countryid: user.countryid,
            countryname: user.countryname,
            isocode: user.isocode,
          }));

          this.totalRows = response.data.query.total;
          this.setUsers(this.items);
          this.setTotalUsers(this.totalRows);
        } else {
          this.items = [];
          this.totalRows = 0;
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to fetch users. Please try again.",
          { position: "top-right" },
        );
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.isLoading = false;
        this.setLoading(false);
      }
    },

    searchUsers() {
      this.currentPage = 1;
      this.fetchUsers();
    },

    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.fetchUsers();
    },

    onEnter() {
      const pageNumber = parseInt(this.currentPageJump);
      const maxPages = Math.ceil(this.totalRows / this.perPage);

      if (pageNumber && pageNumber > 0 && pageNumber <= maxPages) {
        this.currentPage = pageNumber;
        this.currentPageJump = "";
      } else {
        this.$toast.warning(
          `Please enter a valid page number between 1 and ${maxPages}`,
          { position: "top-right" },
        );
      }
    },

    showCreateUserModal() {
      this.$refs.createUserModal.show();
    },

    onUserCreated() {
      this.fetchUsers();
    },

    onUserUpdated() {
      this.fetchUsers();
    },

    isSuperAdmin(role) {
      return !!role && role.toLowerCase() === "superadmin";
    },

    // A non-SuperAdmin may not edit/lock/change-status of a SuperAdmin row.
    canManageRow(item) {
      if (!this.canEdit && !this.canChangeStatus) return false;
      if (this.isSuperAdmin(item.role) && !this.isSuperAdminUser) return false;
      return true;
    },

    isSelf(item) {
      return !!this.currentUserId && item.userid === this.currentUserId;
    },

    isLocked(item) {
      return (item.status || "").toLowerCase() === "locked";
    },

    // The list `status` is a label string; map back to compare with option values.
    isCurrentStatus(item, statusValue) {
      const labelByValue = {
        0: "changepassword",
        1: "active",
        3: "suspend",
        4: "disabled",
      };
      return (item.status || "").toLowerCase() === labelByValue[statusValue];
    },

    showEditUserModal(item) {
      this.$refs.editUserModal.show(item.userid);
    },

    async changeStatus(item, statusValue, label) {
      const result = await this.$swal({
        title: "Are you sure?",
        text: `Set status of ${item.firstname} ${item.lastname} to "${label}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, set to ${label}`,
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
      await this.submitStatus(item, statusValue, `Status updated to "${label}".`);
    },

    async lockUser(item) {
      const result = await this.$swal({
        title: "Lock user?",
        text: `Lock ${item.firstname} ${item.lastname}? They will be unable to log in.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, lock",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
      // 2 = Locked
      await this.submitStatus(item, 2, `${item.firstname} ${item.lastname} has been locked.`);
    },

    async unlockUser(item) {
      const result = await this.$swal({
        title: "Unlock user?",
        text: `Unlock ${item.firstname} ${item.lastname}? Their account will become active.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, unlock",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
      // 1 = Active (also resets failed-login count server-side)
      await this.submitStatus(item, 1, `${item.firstname} ${item.lastname} has been unlocked.`);
    },

    async submitStatus(item, statusValue, successMessage) {
      this.isLoading = true;
      try {
        await InternalUserService.updateUserStatus(item.userid, statusValue);
        this.$toast.success(successMessage, { position: "top-right" });
        // Await the refresh so the loader stays up until the list is reloaded.
        await this.fetchUsers();
      } catch (error) {
        console.error("Error updating user status:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to update user status. Please try again.",
          { position: "top-right" },
        );
      } finally {
        this.isLoading = false;
      }
    },

    async onHqTxToggle(item) {
      // v-model has already flipped the value before @change fires
      const newValue = item.canProcessHqTx;
      const action = newValue ? "enable" : "disable";

      const result = await this.$swal({
        title: "Are you sure?",
        text: `Do you want to ${action} HQ transaction processing for ${item.firstname} ${item.lastname}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${action} it`,
        cancelButtonText: "Cancel",
      });

      // User dismissed/cancelled — revert the checkbox to its previous state
      if (!result.isConfirmed) {
        item.canProcessHqTx = !newValue;
        return;
      }

      this.isLoading = true;
      try {
        await this.$store.dispatch(`internalUser/${UPDATE_INTERNAL_USER_HQ_TX}`, {
          userid: item.userid,
          canProcessHqTx: newValue,
        });
        this.$toast.success(
          `HQ transaction processing ${newValue ? "enabled" : "disabled"} for ${item.firstname} ${item.lastname}.`,
          { position: "top-right" },
        );
      } catch (error) {
        // Revert the optimistic UI change on failure
        item.canProcessHqTx = !newValue;
        console.error("Error updating HQ transaction permission:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to update HQ transaction permission. Please try again.",
          { position: "top-right" },
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.card-top-line {
  border-top: 3px solid #007bff;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35em 0.65em;
}

.table-custom {
  font-size: 0.9rem;
}

.action-top-search {
  min-width: 300px;
}

/* Process HQ checkbox — centered in its cell */
.hq-tx-checkbox {
  width: 1.1rem;
  height: 1.1rem;
  margin: 0;
  cursor: pointer;
  accent-color: #007bff;
  vertical-align: middle;
}
</style>
